/**
 * DashboardConfigModal – Unified Create / Edit modal for dashboard config entries.
 *
 * Filters are passed through as-is from the API — no editing required.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { MultiRoleSelector } from './MultiRoleSelector';
import { DashboardSelector } from './DashboardSelector';
import { DashboardDetailsPreview } from './DashboardDetailsPreview';
import {
  useUserRoles,
  useMetabaseDashboards,
  useDashboardDetails,
  useCreateDashboardConfig,
  useUpdateDashboardConfig,
} from '@/hooks/dashboardConfig/useDashboardConfig';
import type {
  MetabaseFlatResource,
  NormalisedDashboardItem,
  ReferenceUserRole,
  MetabaseDashboardFilter,
} from '@/types/dashboardConfig';

type FilterValueSource = NonNullable<MetabaseDashboardFilter['valueSource']>;

interface DashboardConfigModalProps {
  open: boolean;
  loginUserId: string | number | null;
  initialItem?: NormalisedDashboardItem | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function DashboardConfigModal({
  open,
  loginUserId,
  initialItem,
  onClose,
  onSuccess,
}: DashboardConfigModalProps) {
  // ─── Form state ─────────────────────────────────────────────────────────────
  const [selectedRoles, setSelectedRoles] = useState<ReferenceUserRole[]>([]);
  const [selectedDashboardKey, setSelectedDashboardKey] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<MetabaseFlatResource | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [autoLoadDashboardId, setAutoLoadDashboardId] = useState<number | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [filterSources, setFilterSources] = useState<Record<string, FilterValueSource>>({});

  // ─── Validation errors ───────────────────────────────────────────────────────
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // ─── Data hooks ──────────────────────────────────────────────────────────────
  const { user } = useAuth();
  const currentUserType = (user?.type ?? '').toUpperCase();
  // EA admins can only assign dashboards to the EA role (i.e. themselves).
  const isRoleRestricted = currentUserType === 'EA';
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const effectiveRoles = useMemo(
    () => (isRoleRestricted ? roles.filter((r) => r.role.toUpperCase() === 'EA') : roles),
    [roles, isRoleRestricted],
  );
  const { dashboards, isLoading: dashboardsLoading, error: dashboardsError, refetch: refetchDashboards } = useMetabaseDashboards(loginUserId);
  const {
    details,
    isLoading: detailsLoading,
    error: detailsError,
    retry: retryDetails,
  } = useDashboardDetails(loginUserId, autoLoadDashboardId);
  const { create, isLoading: creating } = useCreateDashboardConfig(loginUserId);
  const { update, isLoading: updating } = useUpdateDashboardConfig(loginUserId);
  const isSaving = creating || updating;

  // ─── Seed tracking refs ──────────────────────────────────────────────────────
  const rolesSeededRef = useRef(false);
  const dashboardSeededRef = useRef(false);
  const filtersSeededRef = useRef(false);
  const isEditMode = Boolean(initialItem);

  // ─── Reset when modal closes ─────────────────────────────────────────────────
  useEffect(() => {
    if (open) return;
    setSelectedRoles([]);
    setSelectedDashboardKey('');
    setSelectedDashboard(null);
    setDisplayName('');
    setIsDefault(false);
    setAutoLoadDashboardId(null);
    setRolesError(null);
    setDashboardError(null);
    setFilterValues({});
    setFilterSources({});
    rolesSeededRef.current = false;
    dashboardSeededRef.current = false;
    filtersSeededRef.current = false;
  }, [open]);

  // ─── Seed edit fields once when modal opens ──────────────────────────────────
  useEffect(() => {
    if (!open || !isEditMode || !initialItem) return;
    setDisplayName(
      initialItem.customName !== initialItem.metabaseQuestionName
        ? initialItem.customName
        : ''
    );
    setIsDefault(initialItem.isDefault);
    setSelectedDashboardKey(`dashboard:${initialItem.resourceId}`);
    setAutoLoadDashboardId(initialItem.resourceId);
    rolesSeededRef.current = false;
    dashboardSeededRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItem?.id]);

  // ─── Seed roles once roles list loads (edit mode only) ───────────────────────
  useEffect(() => {
    if (!open || !isEditMode || !initialItem || roles.length === 0) return;
    if (rolesSeededRef.current) return;
    rolesSeededRef.current = true;
    const matched = roles.filter((r) => initialItem.userTypes.includes(r.role));
    setSelectedRoles(matched);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roles]);

  // ─── EA admins: auto-select EA role and lock the selector ────────────────────
  useEffect(() => {
    if (!open || !isRoleRestricted || effectiveRoles.length === 0) return;
    const eaOnly = effectiveRoles.filter((r) => r.role.toUpperCase() === 'EA');
    setSelectedRoles((prev) => {
      const sameLength = prev.length === eaOnly.length;
      const sameRoles = sameLength && prev.every((p) => eaOnly.some((e) => e.role === p.role));
      return sameRoles ? prev : eaOnly;
    });
  }, [open, isRoleRestricted, effectiveRoles]);

  // ─── Seed dashboard once dashboards list loads (edit mode only) ──────────────
  useEffect(() => {
    if (!open || !isEditMode || !initialItem || dashboards.length === 0) return;
    if (dashboardSeededRef.current) return;
    dashboardSeededRef.current = true;
    const found = dashboards.find((d) => d.resourceId === initialItem.resourceId);
    if (found) setSelectedDashboard(found);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dashboards]);

  // ─── Seed filter values from initialItem once details load (edit mode only) ──
  useEffect(() => {
    if (!open || !isEditMode || !initialItem || !details) return;
    if (filtersSeededRef.current) return;
    filtersSeededRef.current = true;
    const seeded: Record<string, string> = {};
    const seededSources: Record<string, FilterValueSource> = {};
    for (const f of initialItem.dashboardFilters ?? []) {
      if (!f.slug) continue;
      if (f.valueSource) seededSources[f.slug] = f.valueSource;
      if (typeof f.value === 'string' && f.value !== '') {
        seeded[f.slug] = f.value;
        // Backward-compat: if value present without source, treat as static.
        if (!f.valueSource) seededSources[f.slug] = 'static';
      }
    }
    setFilterValues(seeded);
    setFilterSources(seededSources);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, details]);

  // ─── Dashboard change ─────────────────────────────────────────────────────────
  const handleDashboardChange = (key: string, resource: MetabaseFlatResource) => {
    setSelectedDashboardKey(key);
    setSelectedDashboard(resource);
    setDashboardError(null);
    setAutoLoadDashboardId(resource.resourceId);
    // Reset values when switching to a different dashboard's filters
    setFilterValues({});
    setFilterSources({});
    filtersSeededRef.current = true; // prevent re-seed from stale initialItem after manual change
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedDashboard) {
      setDashboardError('Please select a dashboard.');
      return;
    }
    setDashboardError(null);

    const resolvedName = displayName.trim() || selectedDashboard!.displayName;

    // dashboardFilters sent as received from Metabase, with admin-entered `value`
    // merged onto each filter object (keyed by slug).
    const enrichedFilters = (details?.filters ?? []).map((f) => {
      const source = filterSources[f.slug];
      if (!source) return { ...f };
      if (source === 'static') {
        const v = filterValues[f.slug]?.trim();
        return v ? { ...f, valueSource: source, value: v } : { ...f };
      }
      return { ...f, valueSource: source };
    });

    const payload = {
      customName: resolvedName,
      metabaseQuestionName: selectedDashboard!.displayName,
      resourceType: 'dashboard' as const,
      resourceId: Number(selectedDashboard!.resourceId),
      userTypes: selectedRoles.map((r) => r.role),
      userRoleIds: selectedRoles.map((r) => r.role_id),
      dashboardQuestions: details?.questions ?? [],
      dashboardFilters: enrichedFilters,
      isDefault: !!isDefault,
    };

    let result;
    if (isEditMode && initialItem) {
      result = await update(initialItem.id, payload);
    } else {
      result = await create(payload);
    }

    if (result.success) {
      toast({
        title: isEditMode ? 'Dashboard updated' : 'Dashboard saved',
        description: `"${resolvedName}" saved for role(s): ${selectedRoles.map((r) => r.role).join(', ')}.`,
      });
      onSuccess();
      onClose();
    } else {
      toast({
        title: isEditMode ? 'Failed to update' : 'Failed to save',
        description: result.error ?? 'Unknown error occurred.',
        variant: 'destructive',
      });
    }
  };

  const canSave = !!selectedDashboard && !isSaving;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto [&]:top-[50%] [&]:translate-y-[-50%]">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isEditMode ? 'Edit Analytics Dashboard' : 'Add Analytics Dashboard'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update roles, dashboard, display name, or default status.'
              : 'Select role(s) and a Metabase dashboard to add.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* ── Dashboard Selector ── */}
          <div className="space-y-1">
            <DashboardSelector
              dashboards={dashboards}
              isLoading={dashboardsLoading}
              value={selectedDashboardKey}
              onChange={handleDashboardChange}
              error={dashboardsError}
              onRetry={refetchDashboards}
            />
            {dashboardError && (
              <p className="text-xs text-destructive">{dashboardError}</p>
            )}
          </div>

          {/* ── Multi-Role Selector ── */}
          <MultiRoleSelector
            roles={effectiveRoles}
            isLoading={rolesLoading}
            selected={selectedRoles}
            onChange={(r) => {
              setSelectedRoles(r);
              if (r.length > 0) setRolesError(null);
            }}
            disabled={isRoleRestricted}
            error={rolesError}
          />

          {/* ── Dashboard Details Preview (auto-loaded after selection) ── */}
          {autoLoadDashboardId !== null && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">
                Dashboard Preview
              </p>
              <DashboardDetailsPreview
                details={details}
                isLoading={detailsLoading}
                error={detailsError}
                onRetry={retryDetails}
                filterValues={filterValues}
                onFilterValueChange={(slug, value) =>
                  setFilterValues((prev) => ({ ...prev, [slug]: value }))
                }
                filterSources={filterSources}
                onFilterSourceChange={(slug, source) =>
                  setFilterSources((prev) => {
                    const next = { ...prev };
                    if (source === undefined) delete next[slug];
                    else next[slug] = source;
                    return next;
                  })
                }
              />
            </div>
          )}

          {/* ── Display Name ── */}
          <div className="space-y-1.5">
            <Label htmlFor="displayName" className="text-sm font-medium text-foreground">
              Display Name
            </Label>
            <Input
              id="displayName"
              placeholder={
                selectedDashboard
                  ? `Default: "${selectedDashboard.displayName}"`
                  : 'e.g. ES Monthly Overview'
              }
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full"
            />
          </div>

          {/* ── Set as Default ── */}
          <div className="flex items-center gap-3">
            <Switch id="isDefault" checked={isDefault} onCheckedChange={setIsDefault} />
            <Label htmlFor="isDefault" className="text-sm text-foreground cursor-pointer">
              Set as default dashboard for selected role(s)
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSave}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isSaving
              ? isEditMode ? 'Updating…' : 'Saving…'
              : isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
